import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OctagonX, Play, Square } from 'lucide-react';

import { AbortWaveResult, abortWave, completeWave, waveErrorCode } from '../api/waves';
import { Migration, MigrationLicense } from '../api/migrations';
import ActivateWaveDialog from './ActivateWaveDialog';
import BprtStatusBadge from './BprtStatusBadge';
import WaveCanaryPanel from './WaveCanaryPanel';
import WaveFailedDevicesPanel from './WaveFailedDevicesPanel';
import WavePackagePanel from './WavePackagePanel';
import RelativeTime from '../components/RelativeTime';
import { errorText } from '../lib/errorText';
import { STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

import EnvironmentEcho from './EnvironmentEcho';
// FD-286 (FD-ADR-021) — the wave lifecycle, which until now had **no UI at
// all**: activate, complete and abort were Postman calls against a documented
// kill-switch.
//
// Activation moved out to `ActivateWaveDialog` under FD-305: it became a staged
// flow with its own state machine, and this file was already 634 lines. What
// stayed true is the load-bearing part — **a person still has to sign in**. The
// mint runs under the destination tenant's minting service account, not the
// admin session. FD-ADR-032 changed only where the resulting token lives (the
// Manager, never the browser), not who authenticates.

type Dialogs = 'activate' | 'complete' | 'abort' | null;

export interface WavePanelProps {
  migration: Migration;
  // FD-358 (FD-ADR-036 §4) — how many of this wave's devices are held at the
  // IDENTITY_NOT_READY delivery gate (identityStatus === 'not-ready', FD-357).
  // Derived by the parent from the devices it already loaded — the panel stays
  // presentational. Optional: a pre-FD-357 Manager cannot produce it.
  identityHeldCount?: number;
  /**
   * FD-442 (FD-ADR-042 §7) — the deployment's default wave-BPRT lifetime, straight
   * from `get-migration`'s `waveDefaults`. Passed through to the activate dialog so
   * the number it pre-fills is the number the SERVER will apply.
   *
   * Optional: a Manager older than FD-442 does not send it, and the dialog then
   * falls back to the 30 both sides used to hard-code.
   */
  defaultExpiryDays?: number;
}

export default function WavePanel({
  migration,
  identityHeldCount,
  defaultExpiryDays,
}: WavePanelProps) {
  const [open, setOpen] = useState<Dialogs>(null);
  const queryClient = useQueryClient();

  const status = (migration.bprtStatus ?? '').toLowerCase();
  const isActive = status === 'active';
  const neverActivated = !migration.bprtStatus;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['migration', migration.migrationId] });

  return (
    <section
      className="flex flex-col gap-4 p-5"
      style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}
      aria-label="Wave lifecycle"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            className="font-display text-base font-semibold tracking-tight"
            style={{ color: TEXT.primary }}
          >
            Wave
          </h2>
          <p
            className="mt-1 max-w-[62ch] font-display text-[13px]"
            style={{ color: TEXT.secondary }}
          >
            {neverActivated
              ? 'Devices cannot begin migrating until this wave is activated — activation is what mints the credential they join with.'
              : isActive
                ? 'This wave is running. Devices can fetch their flight plans and join the destination tenant.'
                : 'This wave’s credential has been revoked. No further device can join with it.'}
          </p>
        </div>
        <BprtStatusBadge status={migration.bprtStatus} />
      </div>

      {isActive && migration.bprtExpiresUtc && (
        <div className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          Credential expires <RelativeTime iso={migration.bprtExpiresUtc} />{' '}
          <span className="font-mono text-xs">({migration.bprtExpiresUtc})</span>
        </div>
      )}

      {/* FD-435 (FD-ADR-041 decision 8) — the wave's licence ledger. Until
          FD-434 these counts existed on the migration row and nothing could read
          them, so the change of unit from campaign to device was invisible to
          the operator it exists to be legible to. */}
      <WaveLicenseMeter license={migration.license} />

      {/* FD-358 — devices waiting for an identity mapping keep the wave from
          finishing quietly; the count belongs beside the lifecycle controls
          because the RELEASE is an admin act (add the mapping — allowed while
          the wave runs, FD-ADR-036 §3), not a device act. Only rendered while
          the wave is active and the count is real: 0 is the ordinary state and
          needs no line. */}
      {isActive && (identityHeldCount ?? 0) > 0 && (
        <div className="font-display text-[13px]" style={{ color: '#D97706' }}>
          {identityHeldCount === 1
            ? '1 device is waiting for an identity mapping'
            : `${identityHeldCount} devices are waiting for identity mappings`}{' '}
          — held at the identity gate until their users are mapped. Mappings can be added while
          the wave runs.
        </div>
      )}

      {/* Only legal transitions are offered. An inactive wave has nothing to
          complete or abort — offering either would be a button that can only
          fail. */}
      <div className="flex flex-wrap gap-2">
        {!isActive && (
          <Button
            appearance="primary"
            icon={<Play size={16} strokeWidth={1.5} />}
            onClick={() => setOpen('activate')}
          >
            {neverActivated ? 'Activate wave' : 'Re-activate wave'}
          </Button>
        )}
        {isActive && (
          <>
            <Button
              appearance="secondary"
              icon={<Square size={16} strokeWidth={1.5} />}
              onClick={() => setOpen('complete')}
            >
              Complete wave
            </Button>
            <Button
              appearance="secondary"
              icon={<OctagonX size={16} strokeWidth={1.5} />}
              onClick={() => setOpen('abort')}
            >
              Abort wave
            </Button>
          </>
        )}
      </div>

      {/* FD-321 (FD-ADR-034) — the package lives INSIDE this panel rather than
          in a section of its own, because it is the second half of the same
          question: a wave with a credential but no package is not deliverable,
          and a device that discovers that does so past the point of no return.
          Rendered unconditionally, including on an inactive wave, where it says
          what to do first — a state row that disappears when there is no
          package is a state row that never answers "why not". */}
      <WavePackagePanel migration={migration} onDone={invalidate} />

      {/* FD-372 (FD-ADR-035) — the first-device canary hold, while the wave
          runs. The panel renders nothing unless canaryMode === 'on', so a
          pre-FD-371 Manager (field absent) and an opted-out wave both cost no
          pixels. Gated on an ACTIVE wave: the hold only exists while devices
          can fetch plans — on a revoked wave the canary is history, and the
          event trail owns history. */}
      {isActive && <WaveCanaryPanel migration={migration} onReleased={invalidate} />}

      {/* FD-372 — the H2 retry set (FD-369's read), on the completed-wave
          view. Once the wave is revoked, "which devices did NOT make it" is
          the question this panel exists to answer — and the input to moving
          them to a supplementary campaign. */}
      {status === 'revoked' && <WaveFailedDevicesPanel migrationId={migration.migrationId} />}

      {/* Each dialog is mounted CLOSED and toggled open — a Fluent Dialog
          mounted already-open never enters the accessibility tree
          (FLUENT_UI_ADDENDUM). */}
      <ActivateWaveDialog
        open={open === 'activate'}
        migrationId={migration.migrationId}
        // FD-435 — the count the dialog states it will reserve, straight off the
        // server's migration row. Deliberately NOT a length of any device list
        // this console happens to have loaded: that would be a different number
        // from the one FD-430 reserves, asserted with the same confidence.
        deviceCount={migration.deviceCount}
        // FD-442 — the credential lifetime this deployment applies, read from the
        // server rather than mirrored here. Before FD-442 the dialog carried its own
        // '30' beside activate-wave's own 30; either could have moved without the
        // other being wrong, and the only symptom would have been an operator reading
        // two different lifetimes for one credential.
        defaultExpiryDays={defaultExpiryDays}
        // FD-611 — header context; the type renders as Hybrid / Cloud.
        migrationName={migration.migrationName}
        migrationType={migration.migrationType}
        destinationDomain={migration.destTenantDomain}
        onClose={() => setOpen(null)}
        onDone={invalidate}
      />
      <CompleteDialog
        open={open === 'complete'}
        migrationId={migration.migrationId}
        onClose={() => setOpen(null)}
        onDone={invalidate}
      />
      <AbortDialog
        open={open === 'abort'}
        migrationId={migration.migrationId}
        onClose={() => setOpen(null)}
        onDone={invalidate}
      />
    </section>
  );
}

// ——— licence meter ————————————————————————————————————————————————————————

/**
 * FD-435 (FD-ADR-041 decision 8) — "Reserved N · Burned B · Released R".
 *
 * FOUR STATES, AND THE MIDDLE TWO ARE THE ONES THAT CATCH PEOPLE OUT:
 *
 *   `undefined` — this Manager predates FD-434 and projects no licence state.
 *                 Render nothing: there is no claim available to make, and a
 *                 row asserting one would be the FD-269 defect (a field
 *                 reporting a state it cannot know).
 *   `null`      — projected, and the wave was never activated. Nothing was ever
 *                 reserved; the panel above already says the wave is inactive.
 *   null counts — an ACTIVATED wave whose row predates FD-430. It really ran and
 *                 really consumed licences, under the superseded per-campaign
 *                 unit. This MUST read "not tracked" — rendering `0` would
 *                 report a wave as having consumed nothing when the truth is
 *                 that nobody was counting.
 *   numbers     — the per-device ledger. `0` is a REAL value: "reserved nothing"
 *                 and "burned nothing" are answers, not absences.
 *
 * Which is why every test below is `typeof === 'number'`. A `?? 0`, a `|| 0` or
 * a truthiness check anywhere in here collapses the third state into the fourth
 * and is exactly the defect FD-430 (stamping `''`, never `0`) and FD-434
 * (mapping `''` to `null`, never `0`) each guarded on their own side. This is
 * the last link in that chain.
 */
export function WaveLicenseMeter({ license }: { license?: MigrationLicense | null }) {
  // Covers `undefined` (Manager predates FD-434) and `null` (never activated).
  if (!license) return null;

  const { reservedCount, burnedCount, releasedCount } = license;
  const anyCount =
    typeof reservedCount === 'number' ||
    typeof burnedCount === 'number' ||
    typeof releasedCount === 'number';

  if (!anyCount) {
    return (
      <div className="font-display text-[13px]" style={{ color: TEXT.secondary }} aria-label="Migration licenses">
        Migration licenses — <strong>not tracked</strong>. This wave was activated before
        per-device licensing, so its counts were never recorded. It did consume licenses, under
        the superseded per-campaign unit.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5" aria-label="Migration licenses">
      <div className="font-display text-[13px]" style={{ color: TEXT.primary }}>
        <span style={{ color: TEXT.secondary }}>Migration licenses — </span>
        <LicenseCount label="Reserved" value={reservedCount} />
        <span style={{ color: TEXT.secondary }}> · </span>
        <LicenseCount label="Burned" value={burnedCount} />
        <span style={{ color: TEXT.secondary }}> · </span>
        <LicenseCount label="Released" value={releasedCount} />
      </div>
      {(license.bucket || license.mode || license.settledUtc) && (
        <div className="font-display text-xs" style={{ color: TEXT.secondary }}>
          {license.bucket && (
            <>
              bucket <span className="font-mono text-xs">{license.bucket}</span>
            </>
          )}
          {license.bucket && license.mode ? ' · ' : null}
          {license.mode && (
            <>
              unit <span className="font-mono text-xs">{license.mode}</span>
            </>
          )}
          {(license.bucket || license.mode) && license.settledUtc ? ' · ' : null}
          {license.settledUtc && (
            <>
              settled <RelativeTime iso={license.settledUtc} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One count. `typeof value === 'number'` so **`0` renders as `0`** and only a
 * genuinely absent count reads as "not tracked" — see `WaveLicenseMeter`.
 */
function LicenseCount({ label, value }: { label: string; value: number | null }) {
  return (
    <>
      <span style={{ color: TEXT.secondary }}>{label} </span>
      {typeof value === 'number' ? (
        <span className="font-mono tabular-nums" style={{ color: TEXT.primary }}>
          {value}
        </span>
      ) : (
        <span style={{ color: TEXT.secondary }}>not tracked</span>
      )}
    </>
  );
}

// ——— complete ———————————————————————————————————————————————————————————

function CompleteDialog({
  open,
  migrationId,
  onClose,
  onDone,
}: {
  open: boolean;
  migrationId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const mutation = useMutation({
    mutationFn: () => completeWave(migrationId),
    onSuccess: () => {
      onDone();
      onClose();
      mutation.reset();
    },
  });

  const isRetry = waveErrorCode(mutation.error) === 'REVOKE_RETRY';

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) {
          mutation.reset();
          onClose();
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Complete wave</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-4">
              {/* FD-422 — completion deletes an Entra user in this deployment's
                  destination tenant and settles its licence. */}
              <EnvironmentEcho />

              <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
                Completing revokes the wave’s join credential and settles its migration
                license. Devices that have already finished are unaffected; no further device
                can join with this credential.
              </p>

              {mutation.isError &&
                (isRetry ? (
                  // FD-185/FD-187: the wave is NOT revoked. Rendering this as
                  // success is exactly the silent kill-switch no-op those tasks
                  // exist to prevent, so it is a warning with a retry — never a
                  // green tick.
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      <MessageBarTitle>Still revoking — retry in a moment</MessageBarTitle>
                      The credential’s directory user is not visible yet (Entra replication
                      lag). <strong>The wave is not revoked.</strong> Retry shortly; the
                      server refuses to report success until Graph confirms the delete.
                    </MessageBarBody>
                  </MessageBar>
                ) : (
                  <MessageBar intent="error">
                    <MessageBarBody>
                      <MessageBarTitle>Could not complete the wave</MessageBarTitle>
                      {errorText(mutation.error)}
                    </MessageBarBody>
                  </MessageBar>
                ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={() => {
                mutation.reset();
                onClose();
              }}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Completing…' : isRetry ? 'Retry' : 'Complete wave'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ——— abort ——————————————————————————————————————————————————————————————

function AbortDialog({
  open,
  migrationId,
  onClose,
  onDone,
}: {
  open: boolean;
  migrationId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<AbortWaveResult | null>(null);

  const mutation = useMutation({
    mutationFn: () => abortWave(migrationId, reason.trim() || undefined),
    onSuccess: (r) => {
      setResult(r);
      onDone();
    },
  });

  const close = () => {
    setReason('');
    setResult(null);
    mutation.reset();
    onClose();
  };

  const isRetry = waveErrorCode(mutation.error) === 'REVOKE_RETRY';

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && close()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Abort wave</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-4">
              {/* FD-422 — abort is irreversible and terminal-stamps devices. The
                  environment is the first thing to be sure of. */}
              <EnvironmentEcho />

              {result ? (
                <AbortOutcome result={result} />
              ) : (
                <>
                  {/* The single most important sentence in this component.
                      Abort stops what has not started; past the Point of No
                      Return there is no rollback, only salvage (FD-ADR-025 E1),
                      so a device mid-migration keeps going. An operator who
                      reads "abort" as "undo" and clicks it on a running wave is
                      the failure this copy exists to prevent. */}
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      <MessageBarTitle>Abort stops what has not started</MessageBarTitle>
                      It revokes the join credential so no further device can begin, and marks
                      devices that never started as aborted.{' '}
                      <strong>Devices already migrating are not stopped and not reversed</strong>{' '}
                      — they finish or end up needing salvage on their own. This cannot be
                      undone.
                    </MessageBarBody>
                  </MessageBar>

                  <div className="flex flex-col gap-1">
                    <Label htmlFor="abort-reason">Reason (optional)</Label>
                    <Input
                      id="abort-reason"
                      value={reason}
                      onChange={(_, d) => setReason(d.value)}
                      placeholder="Recorded on the wave.aborted event"
                      disabled={mutation.isPending}
                    />
                  </div>

                  {mutation.isError &&
                    (isRetry ? (
                      <MessageBar intent="warning">
                        <MessageBarBody>
                          <MessageBarTitle>Still revoking — retry in a moment</MessageBarTitle>
                          The credential’s directory user is not visible yet.{' '}
                          <strong>The wave is not aborted.</strong> Retry shortly.
                        </MessageBarBody>
                      </MessageBar>
                    ) : (
                      <MessageBar intent="error">
                        <MessageBarBody>
                          <MessageBarTitle>Could not abort the wave</MessageBarTitle>
                          {errorText(mutation.error)}
                        </MessageBarBody>
                      </MessageBar>
                    ))}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            {result ? (
              <Button appearance="primary" onClick={close}>
                Close
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={close} disabled={mutation.isPending}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => mutation.mutate()}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? 'Aborting…' : isRetry ? 'Retry' : 'Abort wave'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function AbortOutcome({ result }: { result: AbortWaveResult }) {
  return (
    <div className="flex flex-col gap-3">
      <MessageBar intent={result.failed.length > 0 ? 'warning' : 'success'}>
        <MessageBarBody>
          <MessageBarTitle>
            {result.failed.length > 0 ? 'Wave aborted, with problems' : 'Wave aborted'}
          </MessageBarTitle>
          The join credential is revoked, so no further device can begin.
        </MessageBarBody>
      </MessageBar>
      <dl className="flex flex-col gap-1.5 font-display text-[13px]">
        <OutcomeRow label="Marked aborted" count={result.aborted.length} />
        {/* Reported, never hidden: these devices are the whole reason abort is
            not an undo. */}
        <OutcomeRow
          label="Still migrating (not stopped)"
          count={result.inProgress.length}
          note="These devices own their own lifecycle and will finish or need salvage."
        />
        <OutcomeRow label="Already finished" count={result.alreadyTerminal.length} />
        {result.failed.length > 0 && (
          <OutcomeRow
            label="Could not be stamped"
            count={result.failed.length}
            note="Their rows were not updated. The credential is still revoked."
            danger
          />
        )}
      </dl>
    </div>
  );
}

function OutcomeRow({
  label,
  count,
  note,
  danger,
}: {
  label: string;
  count: number;
  note?: string;
  danger?: boolean;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="font-mono tabular-nums" style={{ color: danger ? STATUS.error : TEXT.primary }}>
          {count}
        </span>
        <span style={{ color: TEXT.primary }}>{label}</span>
      </div>
      {note && count > 0 && (
        <div className="ml-6 font-display text-xs" style={{ color: TEXT.secondary }}>
          {note}
        </div>
      )}
    </div>
  );
}
